/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 18th 2021, 12:45:06 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { OSAlert, OSAlertProps, OSDialogCloseButton, OSLoadingButton, useToggleState, IconText } from "ui-widgets";

interface PlainDialogProps extends DialogProps {
  title: string;
}

export const PlainDialog = ({ title, children, ...props }: PlainDialogProps) => {
  return (
    <>
      <Dialog aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" {...props}>
        <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
        <DialogContent>{children}</DialogContent>
      </Dialog>
    </>
  );
};

export interface ActionDialogProps
  extends Omit<PlainDialogProps, "open">,
    Partial<Pick<PlainDialogProps, "open">>,
    Required<Pick<OSAlertProps, "severity">> {
  confirmationAction?: () => void;
  onCancel?: () => void;
  renderActions?: (args: { handleClose: () => void }) => React.ReactNode;
  defaultActionsType?: "ContinueCancel" | "Close";
  dialogMessage?: React.ReactNode;
  isPeformingAction?: boolean;
  children?: (args: { handleOpen: () => void }) => React.ReactNode;
}

export const ActionDialog = ({
  children,
  dialogMessage,
  confirmationAction,
  onCancel,
  onClose,
  title,
  severity,
  renderActions,
  isPeformingAction = false,
  defaultActionsType = "ContinueCancel",
  ...genericDialogProps
}: ActionDialogProps) => {
  let defaultActions: React.ReactNode;

  const { handleClose, handleOpen, open } = useToggleState();

  const onCloseDialog = children ? handleClose : onClose;

  switch (defaultActionsType) {
    case "ContinueCancel":
      defaultActions = (
        <>
          <Button
            onClick={(event) => {
              onCancel?.();
              onCloseDialog(event, "escapeKeyDown");
            }}
            variant="text"
            size="large"
            className="os-min-sized-button"
          >
            Cancel
          </Button>
          <OSLoadingButton
            variant="contained"
            isLoading={isPeformingAction}
            onClick={() => {
              confirmationAction();
              children && handleClose();
            }}
            size="large"
            autoFocus
          >
            Continue
          </OSLoadingButton>
        </>
      );
      break;

    case "Close":
      defaultActions = (
        <Button onClick={(event) => onCloseDialog(event, "escapeKeyDown")} size="large" autoFocus>
          Close
        </Button>
      );
      break;

    default:
      defaultActions = null;
  }

  return (
    <>
      {children?.({ handleOpen })}
      <PlainDialog
        className="service-modal-dialog"
        title={title}
        {...genericDialogProps}
        open={children ? open : genericDialogProps.open}
        onClose={onCloseDialog}
      >
        <OSAlert severity={severity}>{dialogMessage}</OSAlert>
        <DialogActions className="flex-center">
          {renderActions?.({ handleClose: () => onCloseDialog({}, "escapeKeyDown") }) ?? defaultActions}
        </DialogActions>
      </PlainDialog>
    </>
  );
};

export interface OSDialogProps extends Partial<DialogProps> {
  open?: boolean;
  popUpTitle?: React.ReactNode;
  popUpDescription?: React.ReactNode;
  renderOpenButton?: (args: { handleOpen: () => void }) => React.ReactNode;
  renderContentWithClose?: (args: { handleClose: () => void }) => React.ReactNode;
}

export const OSDialog = (props: OSDialogProps) => {
  const { handleClose, handleOpen, open } = useToggleState();

  const onCloseDialog = props.renderOpenButton ? handleClose : props.onClose;

  return (
    <>
      {props.renderOpenButton?.({ handleOpen })}

      <Dialog
        fullWidth
        maxWidth={props.maxWidth ?? "md"}
        {...props}
        onClose={onCloseDialog}
        open={props.renderOpenButton ? open : props.open}
      >
        <div>
          {props.popUpTitle && (
            <DialogTitle>
              <div className="os-modal-title">{props.popUpTitle}</div>
            </DialogTitle>
          )}
          {onCloseDialog && <OSDialogCloseButton onClick={(e) => onCloseDialog(e, "backdropClick")} />}
        </div>
        {props.renderContentWithClose?.({ handleClose }) ?? props.children}
      </Dialog>
    </>
  );
};

export type StyledOSDialogProps = Omit<OSDialogProps, "popUpTitle"> &
  Partial<Pick<OSDialogProps, "popUpTitle">> & {
    popUpIcon?: React.ReactNode;
  };

export const StyledOSDialog = ({ popUpTitle, popUpIcon, popUpDescription, ...props }: StyledOSDialogProps) => {
  const children = props.renderContentWithClose ? null : (
    <DialogContent className="os-modal-content">
      {popUpDescription && (
        <Typography variant="h6" className="os-content-description">
          {popUpDescription}
        </Typography>
      )}
      {props.children}
    </DialogContent>
  );

  const renderContentWithClose = props.renderContentWithClose
    ? ({ handleClose }) => (
        <DialogContent className="os-modal-content">
          {popUpDescription && (
            <Typography variant="h6" className="os-content-description">
              {popUpDescription}
            </Typography>
          )}
          {props.renderContentWithClose({ handleClose })}
        </DialogContent>
      )
    : null;

  return (
    <OSDialog
      popUpTitle={
        <Typography>
          <IconText icon={popUpIcon} textComponent={<Typography variant="h5">{popUpTitle}</Typography>} />
        </Typography>
      }
      {...props}
      renderContentWithClose={renderContentWithClose}
    >
      {children}
    </OSDialog>
  );
};
